import { Box, Container, Paper, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { ReactNode } from "react";

const HEADSHED_LOGO_SYMBOL_URL =
  "https://cubestatic.s3.eu-west-1.amazonaws.com/img/hs_logo/Headshed+-+Logosymbol.png";

type LoginLayoutProps = {
  children: ReactNode;
  tenantName?: string;
  poweredByText?: string;
  tenantPoweredByText?: string;
};

export const LoginLayout = ({
  children,
  tenantName,
  poweredByText = "Powered by",
  tenantPoweredByText = "powered by",
}: LoginLayoutProps) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        overflow: "hidden",
        background: `
          radial-gradient(ellipse 70% 60% at 15% 10%, ${alpha(theme.palette.custom.orange, 0.32)} 0%, transparent 55%),
          radial-gradient(ellipse 80% 70% at 85% 90%, ${alpha(theme.palette.custom.viking, 0.45)} 0%, transparent 55%),
          radial-gradient(ellipse 60% 50% at 50% 50%, ${alpha(theme.palette.custom.safran, 0.12)} 0%, transparent 60%),
          linear-gradient(135deg, ${theme.palette.custom.preussiskBla} 0%, #0a1330 100%)
        `,
        py: { xs: 4, sm: 6 },
        px: 2,
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.5'/></svg>\")",
          opacity: 0.07,
          pointerEvents: "none",
          mixBlendMode: "overlay",
        },
      }}
    >
      <Container
        maxWidth="sm"
        sx={{
          position: "relative",
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 440,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              width: "100%",
              border: `1px solid ${alpha("#ffffff", 0.08)}`,
              boxShadow: `
                0 30px 80px -20px rgba(0, 0, 0, 0.55),
                0 0 60px -10px ${alpha(theme.palette.custom.orange, 0.22)},
                0 0 0 1px ${alpha("#ffffff", 0.04)}
              `,
            }}
          >
            {children}
          </Paper>

          <Box
            sx={{
              mt: 1.5,
              display: "inline-flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
              rowGap: 0.25,
              maxWidth: "100%",
              px: 1,
              textAlign: "center",
              opacity: 0.9,
              fontSize: "0.8125rem",
              lineHeight: 1.35,
              color: "white",
            }}
            data-testid="login-powered-by"
          >
            {tenantName ? (
              <Typography
                component="span"
                data-testid="login-tenant-name"
                sx={{
                  fontSize: "inherit",
                  lineHeight: "inherit",
                  fontWeight: 600,
                  minWidth: 0,
                  wordBreak: "break-word",
                }}
              >
                {tenantName}
              </Typography>
            ) : null}
            <Box
              component="span"
              data-testid="login-powered-by-branding"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                flexShrink: 0,
                whiteSpace: "nowrap",
                gap: 0.5,
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontSize: "inherit",
                  lineHeight: "inherit",
                  fontWeight: 400,
                  opacity: 0.85,
                }}
              >
                {tenantName ? tenantPoweredByText : poweredByText}
              </Typography>
              <Typography
                component="span"
                sx={{
                  fontSize: "inherit",
                  lineHeight: "inherit",
                  fontWeight: 600,
                }}
                data-testid="login-headshed-name"
              >
                Headshed
              </Typography>
              <Box
                component="img"
                src={HEADSHED_LOGO_SYMBOL_URL}
                alt=""
                aria-hidden
                sx={{
                  height: "1.125em",
                  width: "auto",
                  objectFit: "contain",
                  display: "block",
                  flexShrink: 0,
                }}
                data-testid="login-headshed-logo"
              />
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};
